import {
    NinchatEvents,
    NinchatProperties,
} from 'api/types'
import React, {
    useEffect,
    useState,
} from 'react'
import {
    getChannelMembers,
    getRealmMembers,
} from 'selectors/ninchat'
import {
    useChannelDetails,
    useChannels,
    useRealm,
} from 'hooks/ninchat'
import {ChannelInvitePeopleModalProps} from './types'
import ChannelInvitePeoplePromiseModalView from './ChannelInvitePeoplePromiseModalView'
import {ChannelMembersList} from 'components/RealmQueues/types'

import {InviteesTemplateItem} from 'components/SearchAutocomplete/types'
import {MembersList} from 'types/members'
import {createAccess} from 'api/action/createAccess'
import {sendAccess} from 'api/action/sendAccess'
import {toast} from 'components/Toast'
import {useAppDispatch} from 'hooks'
import {useSelector} from 'react-redux'
import {useTranslation} from 'react-i18next'

const ChannelInvitePeoplePromiseModal = (props: ChannelInvitePeopleModalProps): React.ReactElement => {
    const {
        channelId, onResolve, onReject,
    } = props

    const {t} = useTranslation()

    const [membersToAdd, setMembersToAdd] = useState<Array<InviteesTemplateItem>>([])
    const [inviteUrl, setInviteUrl] = useState<string>('')

    const dispatch = useAppDispatch()
    const {realm_id} = useChannels([channelId])[channelId]
    const realmMembers = useSelector((state) => getRealmMembers(state, realm_id))
    const channelMembers = useSelector((state) => getChannelMembers(state, channelId))
    const channelDetails = useChannelDetails({channelId})

    const realm = useRealm(realm_id, true)

    const getAccessUrl = (accessKey: NinchatProperties.AccessKey): string => `${window.location.origin}${window.location.pathname}#/i/${accessKey}`

    const generateAccessKey = (realmMember = false) => {
        dispatch(createAccess({
            access_type: 'channel',
            channel_id: channelId,
            // eslint-disable-next-line no-extra-parens
            ...(realmMember ? {realm_member: realmMember} : {}),
        })).unwrap()
            .then(
                (event) => {
                    const {access_key} = event as NinchatEvents.NinchatAccessCreatedEvent
                    if (access_key) {
                        setInviteUrl(getAccessUrl(access_key))
                    }
                },
                () => {
                    setInviteUrl(t('Error on generating channel invite'))
                },
            )
    }

    useEffect(() => {
        const membersNotInChannel = realmMembers
            .filter((member: MembersList[0]) => !channelMembers.find((channelMember: ChannelMembersList[0]) => member.id === channelMember.id))
            .map((member: MembersList[0]) => ({
                action: t('Add'),
                company: member.details.info?.company,
                content: '',
                id: member.id,
                name: member.details.name || '',
                realName: member.details.realname,
                title: member.details.info?.job_title,
            }))
            .sort((a, b) => {
                const field1 = a.name || ''
                const field2 = b.name || ''

                if (field1 === field2) {
                    return 0
                }
                if (field1 > field2) {
                    return 1
                }
                return -1
            })
        setMembersToAdd(membersNotInChannel)
    }, [channelMembers, realmMembers])

    const handleSubmitModal = async ({
        members, realmMembership,
    }: {
        members: Array<InviteesTemplateItem>,
        realmMembership: boolean
      }) => {
        if (channelId) {
            try {
                const {access_key} = await dispatch(createAccess({
                    access_type: 'channel',
                    channel_id: channelId,
                    realm_member: realmMembership,
                })).unwrap()

                await Promise.all([
                    members.forEach((member: InviteesTemplateItem) => dispatch(sendAccess({
                        access_key,
                        // eslint-disable-next-line no-extra-parens
                        ...(member?.additionalData?.type === 'email'
                            ? {
                                identity_name: member.id,
                                identity_type: 'email',
                            }
                            : {user_id: member.id}
                        ),
                    })).unwrap()),
                ]).then(() => {
                    toast.success(t('Invitation sent'))
                },
                () => {
                    toast.error(t('Error sending invitation'))
                })
            } catch (error) {
                toast.error(t('Error sending invitation'))
            }
        }


        // eslint-disable-next-line no-unused-expressions
        onResolve && onResolve()
    }
    const handleCloseModal = () => {
        // eslint-disable-next-line no-unused-expressions
        onReject && onReject()
    }


    return (
        <ChannelInvitePeoplePromiseModalView
            channelName={channelDetails.name}
            closeModal={handleCloseModal}
            generateInvitationLink={generateAccessKey}
            inviteUrl={inviteUrl}
            members={membersToAdd}
            organizationName={realm?.realm_attrs.name}
            submitModal={handleSubmitModal}
        />
    )
}

export default ChannelInvitePeoplePromiseModal
